import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CallButton from "../../../components/crm/CallButton";
import { IconCard, IconClose } from "../../../components/Icons";
import StatusBadge from "../../OnboardingCustomers/components/StatusBadge";
import TraitsBadges from "../../OnboardingCustomers/components/TraitsBadges";
import { LeadStatus } from "../../../types/onboarding";
import { LeadDetail } from "../../../types/leadDetail";

type Props = {
  detail: LeadDetail;
};

const fullName = (d: LeadDetail) =>
  [d.name, d.lastName, d.lastName2]
    .filter((s): s is string => Boolean(s && s.trim().length > 0))
    .join(" ") || "(sin nombre)";

const initials = (d: LeadDetail) => {
  const parts = [d.name, d.lastName].filter(Boolean) as string[];
  if (parts.length === 0) return "?";
  return parts.map((p) => p.trim()[0]?.toUpperCase() ?? "").join("");
};

export default function LeadHeader({ detail }: Props) {
  const navigate = useNavigate();
  const [copiedOtp, setCopiedOtp] = useState(false);

  const handleCopyOtp = async () => {
    if (!detail.otp) return;
    try {
      await navigator.clipboard.writeText(detail.otp);
      setCopiedOtp(true);
      setTimeout(() => setCopiedOtp(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "white",
        borderRadius: "0.6rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.06)",
        padding: "0.9rem 1.1rem",
        marginBottom: "0.75rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.95rem" }}>
        <div
          aria-hidden
          style={{
            width: "2.6rem",
            height: "2.6rem",
            borderRadius: "50%",
            background: "#0069d9",
            color: "white",
            fontSize: "1rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {initials(detail)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: "1.15rem",
                fontWeight: 600,
                color: "#1f2933",
              }}
            >
              {fullName(detail)}
            </h1>
            <StatusBadge status={detail.status as LeadStatus} />
            <span
              style={{
                fontSize: ".7rem",
                fontWeight: 600,
                padding: "0.15rem 0.5rem",
                borderRadius: "999px",
                background: "#e9ecef",
                color: "#495057",
              }}
            >
              {detail.market}
            </span>
            {detail.hasTelesubscription && (
              <span
                style={{
                  fontSize: ".7rem",
                  fontWeight: 600,
                  padding: "0.15rem 0.5rem",
                  borderRadius: "999px",
                  background: "#fff3cd",
                  color: "#856404",
                }}
              >
                Telesuscripción
              </span>
            )}
          </div>
          <div
            style={{
              marginTop: ".25rem",
              fontSize: ".82rem",
              color: "#73879C",
              display: "flex",
              gap: "0.85rem",
              flexWrap: "wrap",
            }}
          >
            <span>
              Lead <strong style={{ color: "#1f2933" }}>#{detail.leadId}</strong>
            </span>
            {detail.email && (
              <a
                href={`mailto:${detail.email}`}
                style={{ color: "#0069d9", textDecoration: "none" }}
              >
                {detail.email}
              </a>
            )}
            {detail.phone && (
              <a
                href={`tel:${detail.phone}`}
                style={{ color: "#0069d9", textDecoration: "none" }}
              >
                {detail.phone}
              </a>
            )}
            <CallButton
              leadUlid={detail.leadUlid}
              phone={detail.phone}
              leadName={fullName(detail)}
            />
            {detail.otp && (
              <button
                type="button"
                onClick={handleCopyOtp}
                title="Copiar OTP"
                style={{
                  background: copiedOtp ? "#1a7f37" : "#f8f9fb",
                  color: copiedOtp ? "white" : "#1f2933",
                  border: "1px solid #dee2e6",
                  borderRadius: ".35rem",
                  padding: "0.1rem 0.5rem",
                  fontSize: ".75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "monospace",
                }}
              >
                OTP {copiedOtp ? "✓" : detail.otp}
              </button>
            )}
            {detail.subscriptionId && (
              <Link
                to={`/subscriptions/${detail.subscriptionId}`}
                title="Ver suscripción + invoices"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  color: "#0069d9",
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                <IconCard style={{ width: ".9rem", height: ".9rem" }} />
                Subscription
              </Link>
            )}
            {detail.policy?.policyNumber && (
              <span
                title="Número de póliza"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.3rem",
                  background: "#e7f1ff",
                  color: "#0a58ca",
                  border: "1px solid #cfe2ff",
                  borderRadius: ".35rem",
                  padding: "0.1rem 0.5rem",
                  fontSize: ".75rem",
                  fontWeight: 600,
                  fontFamily: "monospace",
                }}
              >
                Póliza {detail.policy.policyNumber}
              </span>
            )}
          </div>
          {detail.traits.length > 0 && (
            <div style={{ marginTop: ".4rem" }}>
              <TraitsBadges traits={detail.traits} />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          title="Volver"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "2rem",
            height: "2rem",
            padding: 0,
            borderRadius: "0.45rem",
            background: "#f8f9fb",
            color: "#1f2933",
            border: "1px solid #dee2e6",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <IconClose />
        </button>
      </div>
    </header>
  );
}
